/**
 * BattleCode Demo v1.0.0 — Pre-flight 5-tick mini scenario (TS module)
 *
 * Pre-flight 是 Pre-flight + Runtime 兜底(Q6)的"Pre-flight"层。
 * 屏 2 提交战斗时跑这 5 个 tick 验证玩家 bot:
 *   1. 语法正确(无 SyntaxError)
 *   2. 可跑(think() 不抛异常)
 *   3. 5 tick 总耗时 < 250ms
 *
 * 不验证"动作是否合理"(那是战斗中的事),只验证"能跑 + 不会卡死"。
 *
 * FR-025 (2026-07-06):多地图支持
 *   - 之前 24 个 COVER_POSITIONS 手抄 + 5 tick 都 hardcode (0,12)/(24,12) spawn
 *   - 改 battle_easy.json 忘改这里 → bot 看到的 cover ≠ 屏 4 渲染的 cover
 *   - 现在:makePreFlightTicks(mapId) 工厂,从 map JSON 动态取 spawn + cover
 *   - 屏 2 选哪张图,就传哪个 mapId(默认 battle_easy 兼容旧调用)
 *
 * FR-018 ADR-018.8:cover positions 从 map JSON 单一来源提取
 *   pre-flight 跟屏 4 / 引擎 / bot 看到同一份 cover 列表
 */

import type { BattleState } from "./types";
import { getMapById, extractCoverPositions, type CoverPosition } from "./map-utils";

export interface PreFlightTickDef {
  /** 开发者语言 label(原 label 字段,保留用于 log / 调试)。
   *  例: "tick_0_initial_far_apart" */
  label: string;
  /** 玩家语言 label(2026-07-06 P1-#6 加,屏 2 5 tick 列表展示用)。
   *  例: "远距开局 (0,12)→(24,12)"
   *  - 屏 2 5 tick 主信息流显示这个
   *  - 完整开发者 label 走 hover title 提示(屏 2 P1-#6) */
  playerLabel: string;
  /** 一句话场景描述(玩家视角),屏 2 tooltip + 屏内 help 文本用。
   *  例: "双方在 spawn 点开局,距离 24 > vision=10,谁也看不见谁" */
  description: string;
  state: BattleState;
}

// ============================================================
// FR-025:5 个 tick 的"剧情设计"是固定的(位置/HP/冷却/可见性),
// 但 spawn point + cover positions 从 map JSON 动态取
// ============================================================
interface TickSpec {
  label: string;
  tick: number;
  selfPos: [number, number];
  enemyPos: [number, number];
  selfHealth: number;
  enemyHealth: number;
  selfCooldown: number;
  enemyVisible: boolean;
  enemyLkp: [number, number] | null;
}

// ============================================================
// TICK_SPECS 的"剧情设计"是固定的(位置/HP/冷却/可见性),
// 但 spawn point + cover positions 从 map JSON 动态取
// FR-031(2026-07-06)P1-#6:TICK_SPECS 加 playerLabel + description,
// 屏 2 5 tick 主信息流展示玩家化 label(developer label 保留作 hover title)
// ============================================================
const TICK_SPECS: TickSpec[] = [
  {
    // 开局:双方在 spawn,距离 24 > vision_range 10,谁也看不见谁
    label: "tick_0_initial_far_apart",
    tick: 0,
    selfPos: [0, 12],
    enemyPos: [24, 12],
    selfHealth: 150,
    enemyHealth: 150,
    selfCooldown: 0,
    enemyVisible: false,
    enemyLkp: null,
  },
  {
    // 推进到中场:看到对方(距离 8 ≤ vision 10)
    label: "tick_1_enemy_visible_midway",
    tick: 30,
    selfPos: [8, 12],
    enemyPos: [16, 12],
    selfHealth: 150,
    enemyHealth: 150,
    selfCooldown: 0,
    enemyVisible: true,
    enemyLkp: [16, 12],
  },
  {
    // 冷却中 + 失去视野
    label: "tick_2_enemy_lost_mid_cooldown",
    tick: 60,
    selfPos: [12, 12],
    enemyPos: [20, 12],
    selfHealth: 120,
    enemyHealth: 130,
    selfCooldown: 15,
    enemyVisible: false,
    enemyLkp: [18, 12],
  },
  {
    // 低血量被压着打
    label: "tick_3_low_hp_under_fire",
    tick: 90,
    selfPos: [12, 12],
    enemyPos: [22, 12],
    selfHealth: 40,
    enemyHealth: 100,
    selfCooldown: 10,
    enemyVisible: true,
    enemyLkp: [22, 12],
  },
  {
    // 贴身对射,enemy 残血
    label: "tick_4_close_range_full_hp",
    tick: 120,
    selfPos: [11, 12],
    enemyPos: [13, 12],
    selfHealth: 150,
    enemyHealth: 60,
    selfCooldown: 0,
    enemyVisible: true,
    enemyLkp: [13, 12],
  },
];

/** 玩家语言 label + 描述(P1-#6 屏 2 主信息流展示)
 *  - key 跟 TICK_SPECS[i].label 对齐
 *  - playerLabel:8-12 字符,屏 2 5 tick 卡片主显示
 *  - description:一句话场景描述,屏 2 tooltip / hover title 展示 */
const PLAYER_TICK_LABELS: Record<string, { playerLabel: string; description: string }> = {
  "tick_0_initial_far_apart": {
    playerLabel: "远距开局",
    description: "双方在 spawn 点开局,距离 24 > vision=10,谁也看不见谁。bot 应主动推进。",
  },
  "tick_1_enemy_visible_midway": {
    playerLabel: "中距可见",
    description: "推进到中场,距离 8 ≤ vision=10 看见对方。bot 应决策开火 / 找掩体。",
  },
  "tick_2_enemy_lost_mid_cooldown": {
    playerLabel: "冷却中失视野",
    description: "玩家冷却 15 tick,enemy 跑到 lkp=(18,12) 视野外。bot 应基于 last_known_position 追猎。",
  },
  "tick_3_low_hp_under_fire": {
    playerLabel: "低血被压",
    description: "HP 40/150 残血,被 enemy=100 压着打。bot 应找掩体 / 规避。",
  },
  "tick_4_close_range_full_hp": {
    playerLabel: "贴身对射",
    description: "距离 2 贴身,enemy HP 60 残血。bot 应强火力开火收割。",
  },
};

/** 切比雪夫距离 */
function chebyshev(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}

/** 给定 pos + cover 列表,找最近的 cover(切比雪夫距离)
 *  无 cover 返回 null(L1 测试地图) */
function findNearestCover(
  pos: [number, number],
  covers: CoverPosition[],
): { position: { x: number; y: number }; distance: number } | null {
  if (covers.length === 0) return null;
  let bestPos = covers[0];
  let bestDist = chebyshev(pos[0], pos[1], bestPos[0], bestPos[1]);
  for (let i = 1; i < covers.length; i++) {
    const c = covers[i];
    const d = chebyshev(pos[0], pos[1], c[0], c[1]);
    if (d < bestDist) {
      bestPos = c;
      bestDist = d;
    }
  }
  return {
    position: { x: bestPos[0], y: bestPos[1] },
    distance: bestDist,
  };
}

/** 工厂函数:根据 mapId 生成 5 个 preflight tick
 *  - spawn 来自 map.spawns.team_a[0] / team_b[0]
 *  - cover positions 来自 map 自动提取
 *  - 默认 mapId="battle_easy"(向后兼容) */
export function makePreFlightTicks(mapId: string = "battle_easy"): PreFlightTickDef[] {
  const map = getMapById(mapId);
  const covers = extractCoverPositions(mapId);
  const spawnA = (map.spawns.team_a[0] ?? { x: 0, y: 12 }) as { x: number; y: number };
  const spawnB = (map.spawns.team_b[0] ?? { x: 24, y: 12 }) as { x: number; y: number };
  const mapSize = (map.dimensions ?? { width: 25, height: 25 }) as { width: number; height: number };

  return TICK_SPECS.map((spec) => {
    // spawn 始终来自 map JSON(开局固定,FR-022 ADR-022.2)
    const selfSpawn: [number, number] = [spawnA.x, spawnA.y];
    const enemySpawn: [number, number] = [spawnB.x, spawnB.y];
    // 最近 cover 找玩家当前位置最近的(切比雪夫)
    const nearest = findNearestCover(spec.selfPos, covers);
    // 2026-07-06 P1-#6:透传玩家语言 label + 描述,屏 2 5 tick 主信息流展示
    const playerInfo = PLAYER_TICK_LABELS[spec.label] ?? {
      playerLabel: spec.label,
      description: "",
    };
    return {
      label: spec.label,
      playerLabel: playerInfo.playerLabel,
      description: playerInfo.description,
      state: {
        tick: spec.tick,
        self: {
          team: "team_a",
          tank_index: 0,
          position: { x: spec.selfPos[0], y: spec.selfPos[1] },
          spawn: { x: selfSpawn[0], y: selfSpawn[1] },
          health: spec.selfHealth,
          max_health: 150,
          cooldown_left: spec.selfCooldown,
          attack_cooldown_ticks: 30,
          is_alive: true,
        },
        enemy: {
          tank_index: 0,
          position: { x: spec.enemyPos[0], y: spec.enemyPos[1] },
          max_health: 150,
          is_alive: true,
          spawn: { x: enemySpawn[0], y: enemySpawn[1] },
          health: spec.enemyHealth,
          is_visible: spec.enemyVisible,
          last_known_position: spec.enemyLkp ? { x: spec.enemyLkp[0], y: spec.enemyLkp[1] } : null,
        },
        nearest_cover: nearest,
        map_id: mapId,
        map_size: { width: mapSize.width, height: mapSize.height },
      },
    };
  });
}

// ============================================================
// 向后兼容:旧的 PRE_FLIGHT_TICKS 仍然 export
// (其他文件可能直接 import 它,需要保留)
// 实际值 = makePreFlightTicks() 默认结果(battle_easy)
// ============================================================
export const PRE_FLIGHT_TICKS: PreFlightTickDef[] = makePreFlightTicks("battle_easy");
